import { useEffect, useState } from 'react';

/*
 * How many week columns the contribution grid should draw.
 *
 * This used to be three hardcoded numbers per page — `isSmallMobile ? 7 : 13`
 * below `lg`, otherwise 26 — chosen for no width in particular. On a 390px
 * phone the 8 resulting columns filled 224px of a 310px card and left the
 * right third empty; on an 834px tablet 14 columns filled 388px of 738px.
 * The grid is a row of fixed-size squares, so the number that fits is a
 * function of the measured container, not of a breakpoint band.
 */

/** The `tw-gap-1` between columns. */
const GAP = 4;

/** Fewer than this is not a useful history. */
const MIN_WEEKS = 7;

/** A day cell (`tw-w-6`) and no weekday gutter: the retrospective grid. */
const DEFAULT_CELL_PX = 24;
const DEFAULT_MAX_WEEKS = 26;

export const useWeeksThatFit = (
  container: HTMLElement | null,
  options?: {
    /** Width of one day cell. See `CELL_SIZE_PX` in Grid.tsx. */
    cellPx?: number;
    /** Width taken by the weekday label gutter, if the grid draws one. */
    gutterPx?: number;
    maxWeeks?: number;
  }
) => {
  const cellPx = options?.cellPx ?? DEFAULT_CELL_PX;
  const gutterPx = options?.gutterPx ?? 0;
  const maxWeeks = options?.maxWeeks ?? DEFAULT_MAX_WEEKS;

  /*
   * Start narrow and grow. Starting at the maximum would draw a grid wider
   * than a phone on the first paint, and anything above it that sizes to its
   * content would then measure that inflated width back to us.
   */
  const [weeks, setWeeks] = useState(MIN_WEEKS);

  useEffect(() => {
    if (!container) return;

    const measure = () => {
      // The last column needs no trailing gap, so give the measure that gap back.
      const columns = Math.floor((container.clientWidth - gutterPx + GAP) / (cellPx + GAP));
      // Grid renders weeksToDisplay + 1 columns (0..weeksToDisplay inclusive).
      setWeeks(Math.min(maxWeeks, Math.max(MIN_WEEKS, columns - 1)));
    };

    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [container, cellPx, gutterPx, maxWeeks]);

  return weeks;
};
